import React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';

import type { Size } from '@/components/ui/system/props';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { cn } from '@/lib/utils';

type TabsContextType = {
  variant?: VariantProps<typeof tabsListVariants>['variant'];
  size?: Size;
  fullWidth?: boolean;
};

const TabsContext = React.createContext<TabsContextType>({});

const tabsListVariants = cva(
  'inline-flex min-w-0 items-center rounded-lg p-1.5 text-sm hide-scrollbar bg-muted text-muted-foreground',
  {
    variants: {
      variant: {
        default: 'overflow-x-auto',
        underline: 'mb-1 rounded-none border-b bg-transparent p-0',
      },
      size: {
        sm: 'text-xs gap-1',
        md: 'text-sm gap-1.5',
        lg: 'text-base gap-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

const tabsTriggerVariants = cva(
  "relative inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent px-4 py-2 font-medium whitespace-nowrap text-foreground transition-[color,box-shadow,background,border,opacity,transform] duration-200 ease-out focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 data-[state=inactive]:opacity-90 data-[state=active]:opacity-100 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'bg-transparent text-muted-foreground data-[state=active]:rounded-md data-[state=active]:border-input data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md',
        underline:
          'relative px-4 py-2.5 data-[state=active]:text-foreground after:absolute after:inset-x-0 after:bottom-[-1px] after:z-50 after:h-0.5 after:bg-primary after:origin-left after:scale-x-0 after:transition-transform after:duration-200 data-[state=active]:after:scale-x-100',
      },
      size: {
        sm: 'px-2.5 py-1.5 text-xs',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-2.5 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  },
);

type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> & {
  fullWidth?: boolean;
  variant?: VariantProps<typeof tabsListVariants>['variant'];
  size?: Size;
};

type TabsProps = React.ComponentProps<typeof TabsPrimitive.Root>;

function Tabs({ className, value, onValueChange, ...rest }: TabsProps) {
  const lastForwardedValueRef = React.useRef(value);

  useIsomorphicLayoutEffect(() => {
    lastForwardedValueRef.current = value;
  }, [value]);

  const handleValueChange = (next: string) => {
    if (lastForwardedValueRef.current === next) return;
    lastForwardedValueRef.current = next;
    onValueChange?.(next);
  };

  return (
    <TabsPrimitive.Root
      className={cn('flex flex-col gap-2 md:gap-4', className)}
      value={value}
      onValueChange={onValueChange ? handleValueChange : onValueChange}
      {...rest}
    />
  );
}

function TabsList({
  className,
  variant = 'default',
  fullWidth = true,
  size = 'md',
  ...props
}: TabsListProps) {
  return (
    <TabsContext value={{ variant, size, fullWidth }}>
      <TabsPrimitive.List
        className={cn(tabsListVariants({ variant, size }), !fullWidth && 'max-w-fit', className)}
        {...props}
      />
    </TabsContext>
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { variant, size, fullWidth } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Trigger
      className={cn(tabsTriggerVariants({ variant, size }), fullWidth && 'flex-1', className)}
      {...props}
    />
  );
}

type TabsContentProps = React.ComponentProps<typeof TabsPrimitive.Content>;

function TabsContent({ className, ...props }: TabsContentProps) {
  return <TabsPrimitive.Content className={cn('flex-1 outline-none', className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
